import React, { useState, useEffect } from 'react';
import { ClockIcon, BoltIcon, CurrencyDollarIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline';
import { chargingSessionApi } from '../services/api';

interface ChargingSession {
  id: string;
  sessionId: string;
  stationId: string;
  connectorId: number;
  transactionId: string;
  idTag: string;
  startTime: string;
  endTime?: string;
  status: 'STARTED' | 'CHARGING' | 'SUSPENDED_EV' | 'SUSPENDED_EVSE' | 'STOPPED' | 'FAULTED';
  energyConsumed: number;
  currentPower: number;
  totalCost: number;
  meterStart: number;
  meterStop?: number;
  stopReason?: string;
  createdAt: string;
  updatedAt: string;
}

const SessionsView: React.FC = () => {
  const [sessions, setSessions] = useState<ChargingSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | 'active' | 'completed'>('all');

  useEffect(() => {
    fetchSessions();
  }, []);

  const fetchSessions = async () => {
    setLoading(true);
    try {
      const data = await chargingSessionApi.getAllSessions();
      setSessions(data);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch sessions:', err);
      setError('Failed to load charging sessions');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'STARTED':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'CHARGING':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'SUSPENDED_EV':
      case 'SUSPENDED_EVSE':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'STOPPED':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      case 'FAULTED':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const formatDuration = (startTime: string, endTime?: string) => {
    const start = new Date(startTime);
    const end = endTime ? new Date(endTime) : new Date();
    const durationMs = end.getTime() - start.getTime();
    const minutes = Math.floor(durationMs / (1000 * 60));
    const hours = Math.floor(minutes / 60);

    if (hours > 0) {
      return `${hours}h ${minutes % 60}m`;
    }
    return `${minutes}m`;
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  // ✅ UPDATED: Filter, Sort by newest first, and limit to 3
  const displaySessions = sessions
    .filter(session => {
      switch (filter) {
        case 'active':
          return ['STARTED', 'CHARGING', 'SUSPENDED_EV', 'SUSPENDED_EVSE'].includes(session.status);
        case 'completed':
          return ['STOPPED', 'FAULTED'].includes(session.status);
        default:
          return true;
      }
    })
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()) // Sort newest to oldest
    .slice(0, 3); // Take only the first 3

  if (loading) {
    return (
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex items-center justify-center h-32">
          <div className="text-gray-500">Loading sessions...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white shadow rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        
        <div className="flex items-center space-x-4">
          <h2 className="text-lg font-medium text-gray-900">Charging Sessions</h2>
          <button
            onClick={fetchSessions}
            className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            Refresh
          </button>
        </div>

        {/* Filter Tabs */}
        <div className="flex space-x-1 bg-gray-100 rounded-lg p-1">
          {[
            { key: 'all', label: 'All', count: sessions.length },
            { key: 'active', label: 'Active', count: sessions.filter(s => ['STARTED', 'CHARGING', 'SUSPENDED_EV', 'SUSPENDED_EVSE'].includes(s.status)).length },
            { key: 'completed', label: 'Completed', count: sessions.filter(s => ['STOPPED', 'FAULTED'].includes(s.status)).length }
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => setFilter(tab.key as any)}
              className={`px-3 py-2 rounded-md text-sm font-medium ${filter === tab.key
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
                }`}
            >
              {tab.label} ({tab.count})
            </button>
          ))}
        </div>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-center">
            <ExclamationCircleIcon className="h-5 w-5 text-red-400 mr-2" />
            <span className="text-red-800">{error}</span>
          </div>
        </div>
      )}

      {displaySessions.length === 0 ? (
        <div className="text-center py-8">
          <div className="text-gray-500">No sessions found</div>
        </div>
      ) : (
        <div className="space-y-4">
          {displaySessions.map(session => (
            <div key={session.id} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center space-x-3">
                  <div className="font-medium text-gray-900">
                    {session.stationId} - Connector {session.connectorId}
                  </div>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getStatusColor(session.status)}`}>
                    {session.status}
                  </span>
                </div>
                <div className="text-sm text-gray-500">
                  User: {session.idTag}
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-3">
                <div className="flex items-center space-x-2">
                  <ClockIcon className="h-4 w-4 text-gray-400" />
                  <div>
                    <div className="text-sm font-medium text-gray-900">Duration</div>
                    <div className="text-sm text-gray-600">
                      {formatDuration(session.startTime, session.endTime)}
                    </div>
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  <BoltIcon className="h-4 w-4 text-gray-400" />
                  <div>
                    <div className="text-sm font-medium text-gray-900">Energy</div>
                    <div className="text-sm text-gray-600">
                      {(session.energyConsumed ?? 0).toFixed(2)} kWh
                    </div>
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  <BoltIcon className="h-4 w-4 text-gray-400" />
                  <div>
                    <div className="text-sm font-medium text-gray-900">Power</div>
                    <div className="text-sm text-gray-600">
                      {((session.currentPower ?? 0) / 1000).toFixed(1)} kW
                    </div>
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  <CurrencyDollarIcon className="h-4 w-4 text-gray-400" />
                  <div>
                    <div className="text-sm font-medium text-gray-900">Cost</div>
                    <div className="text-sm text-gray-600">
                      {formatCurrency(session.totalCost)}
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between text-sm text-gray-500">
                <div>
                  Started: {new Date(session.startTime).toLocaleString()}
                </div>
                {session.endTime && (
                  <div>
                    Ended: {new Date(session.endTime).toLocaleString()}
                  </div>
                )}
              </div>

              {session.stopReason && (
                <div className="mt-2 text-sm text-gray-600">
                  Stop Reason: {session.stopReason}
                </div>
              )}

              {/* Transaction Details */}
              <div className="mt-3 pt-3 border-t border-gray-100">
                <div className="flex items-center justify-between text-xs text-gray-400">
                  <span>Session ID: {session.sessionId.substring(0, 8)}...</span>
                  <span>Transaction: {session.transactionId.substring(0, 8)}...</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Summary Statistics */}
      <div className="mt-6 pt-6 border-t border-gray-200">
        <div className="grid grid-cols-3 gap-4">
          <div className="text-center">
            <div className="text-lg font-semibold text-gray-900">
              {sessions.filter(s => ['STARTED', 'CHARGING', 'SUSPENDED_EV', 'SUSPENDED_EVSE'].includes(s.status)).length}
            </div>
            <div className="text-sm text-gray-600">Active Sessions</div>
          </div>
          <div className="text-center">
            <div className="text-lg font-semibold text-gray-900">
              {sessions.reduce((sum, s) => sum + s.energyConsumed, 0).toFixed(1)} kWh
            </div>
            <div className="text-sm text-gray-600">Total Energy</div>
          </div>
          <div className="text-center">
            <div className="text-lg font-semibold text-gray-900">
              {formatCurrency(sessions.reduce((sum, s) => sum + s.totalCost, 0))}
            </div>
            <div className="text-sm text-gray-600">Total Revenue</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SessionsView;